// @greenlight-spec: 28-it-dashboard-ui.md
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

import { usePageTitle } from '@/components/app-shell/AppShell';
import { AgentFeedbackTab } from '@/components/feedback/AgentFeedbackTab';
import { AppCrashesTab } from '@/components/feedback/AppCrashesTab';
import { HealthAlertsTab } from '@/components/feedback/HealthAlertsTab';
import { PipelineFailuresTab } from '@/components/feedback/PipelineFailuresTab';
import { SegmentedTabs } from '@/components/greenlight/SegmentedTabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { api } from '@/lib/api';
import { useCurrentUser } from '@/lib/auth';

const TABS = [
  { key: 'feedback', label: 'Agent feedback' },
  { key: 'pipelines', label: 'Pipeline failures' },
  { key: 'crashes', label: 'App crashes' },
  { key: 'alerts', label: 'Health alerts' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

const isTabKey = (v: string | null): v is TabKey => TABS.some((t) => t.key === v);

export function FeedbackPage(): React.JSX.Element {
  usePageTitle('Feedback');
  const user = useCurrentUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const summaryQuery = api.feedback.summary.useQuery(['feedback-summary'], {});

  const tabParam = searchParams.get('tab');
  const activeTab: TabKey = isTabKey(tabParam) ? tabParam : 'feedback';

  // Org-admin only (docs/28 §/feedback). Nav hides it for other roles and the
  // server 403s the reads; this self-guard covers a direct navigation.
  if (user && user.role !== 'org_admin') {
    return (
      <Alert>
        <AlertDescription>Feedback triage is available to org admins only.</AlertDescription>
      </Alert>
    );
  }

  const unreadCount = summaryQuery.data?.status === 200 ? summaryQuery.data.body.unread_count : 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h1 className="text-lg font-semibold">Feedback</h1>
          {unreadCount > 0 && (
            <span
              data-testid="feedback-unread-count"
              className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[hsl(var(--status-amber)/0.18)] px-1.5 font-mono text-[11px] text-[hsl(var(--status-amber-text))]"
              aria-label={`${unreadCount} unread`}
            >
              {unreadCount}
            </span>
          )}
        </div>
      </div>

      <SegmentedTabs
        items={TABS.map((t) => ({
          value: t.key,
          label: t.label,
          testId: `feedback-tab-${t.key}`,
          id: `feedback-tab-${t.key}`,
          controls: `feedback-panel-${t.key}`,
        }))}
        value={activeTab}
        onValueChange={(v) => {
          const next = new URLSearchParams(searchParams);
          if (v === 'feedback') next.delete('tab');
          else next.set('tab', v);
          setSearchParams(next, { replace: true, preventScrollReset: true });
        }}
        aria-label="Feedback views"
        className="w-full sm:w-auto"
      />

      <div
        role="tabpanel"
        id={`feedback-panel-${activeTab}`}
        aria-labelledby={`feedback-tab-${activeTab}`}
      >
        {activeTab === 'feedback' && <AgentFeedbackTab />}
        {activeTab === 'pipelines' && <PipelineFailuresTab />}
        {activeTab === 'crashes' && <AppCrashesTab />}
        {activeTab === 'alerts' && <HealthAlertsTab />}
      </div>
    </div>
  );
}
